import { CssColor, StyleType, wixColor } from '@wix/editor-platform-ooi-metadata-resolver';
import { createStylesParams, StyleParamType, wixColorParam, wixFontParam } from '@wix/tpa-settings';
import { CustomCssVarsFn } from '@wix/yoshi-flow-editor';

export enum Alignment {
  Left = 0,
  Center = 1,
  Right = 2,
}

export enum RewardsLayout {
  List = 0,
  Cards = 1,
}

export type IStylesParams = {
  pageTitleFont: StyleParamType.Font;
  pageTitleColor: StyleParamType.Color;
  pageDescriptionFont: StyleParamType.Font;
  pageDescriptionColor: StyleParamType.Color;
  rewardsListSectionTitleFont: StyleParamType.Font;
  rewardsListSectionTitleColor: StyleParamType.Color;

  totalPointsTotalNumberFont: StyleParamType.Font;
  totalPointsTotalNumberColor: StyleParamType.Color;
  totalPointsTitleFont: StyleParamType.Font;
  totalPointsTitleColor: StyleParamType.Color;
  totalPointsExpirationFont: StyleParamType.Font;
  totalPointsExpirationColor: StyleParamType.Color;
  totalPointsLinkFont: StyleParamType.Font;
  totalPointsLinkColor: StyleParamType.Color;

  tiersCurrentTierTitleFont: StyleParamType.Font;
  tiersCurrentTierTitleColor: StyleParamType.Color;
  tiersTierNameFont: StyleParamType.Font;
  tiersTierNameColor: StyleParamType.Color;
  tiersProgressBarFont: StyleParamType.Font;
  tiersProgressBarColor: StyleParamType.Color;

  rewardsTotalNumberFont: StyleParamType.Font; // Used as default for "totalPointsTotalNumberFont"
  rewardsTotalNumberColor: StyleParamType.Color; // Used as default for "totalPointsTotalNumberColor"
  rewardsSectionTitleFont: StyleParamType.Font; // Used as default for "rewardsListSectionTitleFont"
  rewardsSectionTitleColor: StyleParamType.Color; // Used as default for "rewardsListSectionTitleColor"
  rewardsRewardTitleFont: StyleParamType.Font;
  rewardsRewardTitleColor: StyleParamType.Color;
  rewardsRewardTextFont: StyleParamType.Font;
  rewardsRewardTextColor: StyleParamType.Color;

  emptyStateTitleFont: StyleParamType.Font;
  emptyStateTitleColor: StyleParamType.Color;
  emptyStateSubtitleFont: StyleParamType.Font;
  emptyStateSubtitleColor: StyleParamType.Color;

  primaryButtonTextFont: StyleParamType.Font;
  primaryButtonTextColor: StyleParamType.Color;
  primaryButtonBorderRadius: StyleParamType.Number;
  primaryButtonBorderWidth: StyleParamType.Number;
  primaryButtonBorderColor: StyleParamType.Color;
  primaryButtonBackgroundColor: StyleParamType.Color;

  secondaryButtonTextFont: StyleParamType.Font;
  secondaryButtonTextColor: StyleParamType.Color;
  secondaryButtonBorderRadius: StyleParamType.Number;
  secondaryButtonBorderWidth: StyleParamType.Number;
  secondaryButtonBorderColor: StyleParamType.Color;
  secondaryButtonBackgroundColor: StyleParamType.Color;

  buttonBackgroundColor: StyleParamType.Color; // Used as default for "primaryButtonBackgroundColor"
  buttonBorderColor: StyleParamType.Color; // Used as default for "primaryButtonBorderColor"
  buttonBorderWidth: StyleParamType.Number; // Used as default for "primaryButtonBorderWidth"
  buttonBorderRadius: StyleParamType.Number; // Used as default for "primaryButtonBorderRadius"
  buttonTextFont: StyleParamType.Font; // Used as default for "primaryButtonTextFont" and "secondaryButtonTextFont"
  buttonTextColor: StyleParamType.Color; // Used as default for "primaryButtonTextColor"

  progressBarTextFont: StyleParamType.Font;
  progressBarTextColor: StyleParamType.Color;
  progressBarColor: StyleParamType.Color;
  progressBarBackgroundColor: StyleParamType.Color;

  badgeTextFont: StyleParamType.Font;
  badgeTextColor: StyleParamType.Color;
  badgeBackgroundColor: StyleParamType.Color;

  secondaryBadgeTextFont: StyleParamType.Font;
  secondaryBadgeTextColor: StyleParamType.Color;
  secondaryBadgeBackgroundColor: StyleParamType.Color;

  backgroundColor: StyleParamType.Color;
  backgroundBorderWidth: StyleParamType.Number;
  backgroundBorderColor: StyleParamType.Color;

  rewardsBackgroundColor: StyleParamType.Color;
  rewardsBackgroundBorderWidth: StyleParamType.Number;
  rewardsBackgroundBorderColor: StyleParamType.Color; // Used as default for "pointsBackgroundBorderColor"

  pointsBackgroundBorderColor: StyleParamType.Color;
  totalPointsBorderRadius: StyleParamType.Number;

  rewardsPendingBackgroundColor: StyleParamType.Color;
  rewardsPendingBorderWidth: StyleParamType.Number;
  rewardsPendingBorderColor: StyleParamType.Color;
  rewardsPendingBorderRadius: StyleParamType.Number;
  rewardsBorderChanged: StyleParamType.Boolean;

  dividerColor: StyleParamType.Color;
  dividerWidth: StyleParamType.Number;
  dividerChanged: StyleParamType.Boolean;

  rewardsLayout: StyleParamType.Number;

  listSpacing: StyleParamType.Number;
  listRewardsHorizontalPadding: StyleParamType.Number;
  listRewardsVerticalPadding: StyleParamType.Number;

  cardsSpacing: StyleParamType.Number;
  cardsRewardsHorizontalPadding: StyleParamType.Number;
  cardsRewardsVerticalPadding: StyleParamType.Number;
  cardsItemsAlignment: StyleParamType.Number;
};

export default createStylesParams<IStylesParams>({
  pageTitleFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Page-title', {
      size: 26,
    }),
  },
  pageTitleColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
      ai: { description: 'Text color of the My Rewards page title' },
    },
  },
  pageDescriptionFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {
      size: 15,
    }),
  },
  pageDescriptionColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
      ai: { description: 'Text color of the My Rewards page description' },
    },
  },
  rewardsListSectionTitleFont: {
    type: StyleParamType.Font,
    getDefaultValue: (params) => {
      const sectionTitle = params.getStyleParamValue({ type: StyleParamType.Font, key: 'rewardsSectionTitleFont' });
      return (
        sectionTitle ??
        wixFontParam('Body-M', {
          size: 20,
        })(params)
      );
    },
  },
  rewardsListSectionTitleColor: {
    type: StyleParamType.Color,
    getDefaultValue: (params) => {
      const sectionTitle = params.getStyleParamValue({ type: StyleParamType.Color, key: 'rewardsSectionTitleColor' });
      return sectionTitle ?? wixColorParam('color-5')(params);
    },
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
      ai: { description: 'Text color of the rewards list section title' },
    },
  },

  totalPointsTotalNumberFont: {
    type: StyleParamType.Font,
    getDefaultValue: (params) => {
      const totalNumber = params.getStyleParamValue({ type: StyleParamType.Font, key: 'rewardsTotalNumberFont' });
      return (
        totalNumber ??
        wixFontParam('Body-M', {
          size: 40,
        })(params)
      );
    },
  },
  totalPointsTotalNumberColor: {
    type: StyleParamType.Color,
    getDefaultValue: (params) => {
      const totalNumber = params.getStyleParamValue({ type: StyleParamType.Color, key: 'rewardsTotalNumberColor' });
      return totalNumber ?? wixColorParam('color-5')(params);
    },
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
      ai: { description: 'Text color of the total points number' },
    },
  },
  totalPointsTitleFont: {
    type: StyleParamType.Font,
    getDefaultValue: (params) => {
      const totalTitle = params.getStyleParamValue({ type: StyleParamType.Font, key: 'rewardsListSectionTitleFont' });
      return (
        totalTitle ??
        wixFontParam('Body-M', {
          size: 16,
        })(params)
      );
    },
  },
  totalPointsTitleColor: {
    type: StyleParamType.Color,
    getDefaultValue: (params) => {
      const totalTitle = params.getStyleParamValue({ type: StyleParamType.Color, key: 'rewardsListSectionTitleColor' });
      return totalTitle ?? wixColorParam('color-5')(params);
    },
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
      ai: { description: 'Text color of the total points label' },
    },
  },
  totalPointsExpirationFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {
      size: 14,
    }),
  },
  totalPointsExpirationColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
      ai: { description: 'Text color of the points expiration message' },
    },
  },
  totalPointsLinkFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', { size: 15 }),
  },
  totalPointsLinkColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstLinksAndActionsColor),
      ai: { description: 'Text color of the learn how to earn points link' },
    },
  },

  tiersCurrentTierTitleFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', { size: 14 }),
  },
  tiersCurrentTierTitleColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
      ai: { description: 'Text color of the current tier label' },
    },
  },
  tiersTierNameFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', { size: 24 }),
  },
  tiersTierNameColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
      ai: { description: 'Text color of the tier name' },
    },
  },
  tiersProgressBarFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', { size: 16 }),
  },
  tiersProgressBarColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
      ai: { description: 'Text color of the tier progress bar' },
    },
  },

  rewardsTotalNumberFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Title', {
      size: 40,
    }),
  },
  rewardsTotalNumberColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
      ai: { description: 'Text color of the rewards total points number' },
    },
  },
  rewardsSectionTitleFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {
      size: 20,
    }),
  },
  rewardsSectionTitleColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
      ai: { description: 'Text color of the rewards section title' },
    },
  },
  rewardsRewardTitleFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {
      size: 16,
    }),
  },
  rewardsRewardTitleColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
      ai: { description: 'Text color of reward item titles' },
    },
  },
  rewardsRewardTextFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {
      size: 15,
    }),
  },
  rewardsRewardTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-4'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
      ai: { description: 'Text color of reward item descriptions' },
    },
  },

  emptyStateTitleFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Page-title', {
      size: 24,
    }),
  },
  emptyStateTitleColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
      ai: { description: 'Text color of the empty state title' },
    },
  },
  emptyStateSubtitleFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {
      size: 16,
    }),
  },
  emptyStateSubtitleColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
      ai: { description: 'Text color of the empty state subtitle' },
    },
  },

  primaryButtonTextFont: {
    type: StyleParamType.Font,
    getDefaultValue: (params) => {
      const button = params.getStyleParamValue({ type: StyleParamType.Font, key: 'buttonTextFont' });
      return (
        button ??
        wixFontParam('Body-M', {
          size: 16,
        })(params)
      );
    },
  },
  primaryButtonTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: (params) => {
      const button = params.getStyleParamValue({ type: StyleParamType.Color, key: 'buttonTextColor' });
      return button ?? wixColorParam('color-1')(params);
    },
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstPrimaryBackgroundColor),
      ai: { description: 'Text color of the primary action button' },
    },
  },
  primaryButtonBorderRadius: {
    type: StyleParamType.Number,
    getDefaultValue: (params) => {
      const button = params.getStyleParamValue({ type: StyleParamType.Number, key: 'buttonBorderRadius' });
      return button ?? 0;
    },
  },
  primaryButtonBorderWidth: {
    type: StyleParamType.Number,
    getDefaultValue: (params) => {
      const button = params.getStyleParamValue({ type: StyleParamType.Number, key: 'buttonBorderWidth' });
      return button ?? 0;
    },
  },
  primaryButtonBorderColor: {
    type: StyleParamType.Color,
    getDefaultValue: (params) => {
      const button = params.getStyleParamValue({ type: StyleParamType.Color, key: 'buttonBorderColor' });
      return button ?? wixColorParam('color-8')(params);
    },
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstLinksAndActionsColor),
      ai: { description: 'Border color of the primary action button' },
    },
  },
  primaryButtonBackgroundColor: {
    type: StyleParamType.Color,
    getDefaultValue: (params) => {
      const button = params.getStyleParamValue({ type: StyleParamType.Color, key: 'buttonBackgroundColor' });
      return button ?? wixColorParam('color-8')(params);
    },
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstLinksAndActionsColor),
      ai: { description: 'Background color of the primary action button' },
    },
  },

  secondaryButtonTextFont: {
    type: StyleParamType.Font,
    getDefaultValue: (params) => {
      const button = params.getStyleParamValue({ type: StyleParamType.Font, key: 'buttonTextFont' });
      return (
        button ??
        wixFontParam('Body-M', {
          size: 16,
        })(params)
      );
    },
  },
  secondaryButtonTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstLinksAndActionsColor),
      ai: { description: 'Text color of the secondary action button' },
    },
  },
  secondaryButtonBorderRadius: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
  },
  secondaryButtonBorderWidth: {
    type: StyleParamType.Number,
    getDefaultValue: () => 1,
  },
  secondaryButtonBorderColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstLinksAndActionsColor),
      ai: { description: 'Border color of the secondary action button' },
    },
  },
  secondaryButtonBackgroundColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstPrimaryBackgroundColor),
      ai: { description: 'Background color of the secondary action button' },
    },
  },

  buttonBackgroundColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstLinksAndActionsColor),
      ai: { description: 'Background color of action buttons' },
    },
  },
  buttonBorderColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstLinksAndActionsColor),
      ai: { description: 'Border color of action buttons' },
    },
  },
  buttonBorderWidth: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
  },
  buttonBorderRadius: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
  },
  buttonTextFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {
      size: 14,
    }),
  },
  buttonTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstPrimaryBackgroundColor),
      ai: { description: 'Text color of action buttons' },
    },
  },

  progressBarTextFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {
      size: 16,
    }),
  },
  progressBarTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
      ai: { description: 'Text color of the progress bar label' },
    },
  },
  progressBarColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstLinksAndActionsColor),
      ai: { description: 'Fill color of the progress bar' },
    },
  },
  progressBarBackgroundColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-4', 0.3),
  },

  badgeTextFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {
      size: 12,
    }),
  },
  badgeTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
      ai: { description: 'Text color of reward badges' },
    },
  },
  badgeBackgroundColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5', 0.06),
  },

  secondaryBadgeTextFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {
      size: 12,
    }),
  },
  secondaryBadgeTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
      ai: { description: 'Text color of secondary reward badges' },
    },
  },
  secondaryBadgeBackgroundColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstPrimaryBackgroundColor),
      ai: { description: 'Background color of secondary reward badges' },
    },
  },

  backgroundColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstPrimaryBackgroundColor),
      ai: { description: 'Background color of the My Rewards page' },
    },
  },
  backgroundBorderWidth: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
  },
  backgroundBorderColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
  },

  rewardsBackgroundColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8', 0.2),
  },
  rewardsBackgroundBorderWidth: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
  },
  rewardsBackgroundBorderColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5', 0),
  },

  pointsBackgroundBorderColor: {
    type: StyleParamType.Color,
    getDefaultValue: (params) => {
      const borderColor = params.getStyleParamValue({
        type: StyleParamType.Color,
        key: 'rewardsBackgroundBorderColor',
      });
      return borderColor ?? wixColorParam('color-5', 1)(params);
    },
  },
  totalPointsBorderRadius: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
  },

  rewardsPendingBackgroundColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8', 0),
  },
  rewardsPendingBorderWidth: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: { type: 'static', value: 0 },
      ai: { description: 'Border width of individual reward items in pixels' },
    },
  },
  rewardsPendingBorderColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.borderColor,
      defaultValue: wixColor(CssColor.WstBoxPrimaryBorderBottomColor),
      ai: { description: 'Border color of individual reward items' },
    },
  },
  rewardsPendingBorderRadius: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
  },
  rewardsBorderChanged: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => false,
  },

  dividerColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5', 0.3),
  },
  dividerWidth: {
    type: StyleParamType.Number,
    getDefaultValue: () => 1,
  },
  dividerChanged: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => false,
  },

  rewardsLayout: {
    type: StyleParamType.Number,
    getDefaultValue: () => RewardsLayout.List,
    metadata: {
      type: StyleType.numberEnum,
      defaultValue: { type: 'static', value: RewardsLayout.List },
      ai: {
        description: 'Rewards layout: 0 - List, 1 - Cards',
      },
    },
  },

  listSpacing: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.gap,
      defaultValue: { type: 'static', value: 0 },
      ai: { description: 'Spacing between reward items in list layout in pixels' },
    },
  },
  listRewardsHorizontalPadding: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.paddingRight,
      defaultValue: { type: 'static', value: 0 },
      ai: { description: 'Horizontal padding of reward items in list layout in pixels' },
    },
  },
  listRewardsVerticalPadding: {
    type: StyleParamType.Number,
    getDefaultValue: () => 30,
    metadata: {
      type: StyleType.paddingBottom,
      defaultValue: { type: 'static', value: 30 },
      ai: { description: 'Vertical padding of reward items in list layout in pixels' },
    },
  },

  cardsSpacing: {
    type: StyleParamType.Number,
    getDefaultValue: () => 24,
    metadata: {
      type: StyleType.gap,
      defaultValue: { type: 'static', value: 24 },
      ai: { description: 'Spacing between reward cards in cards layout in pixels' },
    },
  },
  cardsRewardsHorizontalPadding: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.padding,
      defaultValue: { type: 'static', value: 0 },
      ai: { description: 'Horizontal padding of reward items in cards layout in pixels' },
    },
  },
  cardsRewardsVerticalPadding: {
    type: StyleParamType.Number,
    getDefaultValue: ({ isMobile }) => (isMobile ? 42 : 64),
    metadata: {
      type: StyleType.padding,
      defaultValue: { type: 'static', value: 64 },
      ai: { description: 'Vertical padding of reward items in cards layout in pixels' },
    },
  },
  cardsItemsAlignment: {
    type: StyleParamType.Number,
    getDefaultValue: () => Alignment.Center,
    metadata: {
      type: StyleType.numberEnum,
      defaultValue: { type: 'static', value: Alignment.Center },
      ai: {
        description: 'Reward card content alignment in cards layout: 0 - Left, 1 - Center, 2 - Right',
      },
    },
  },
});

export const customCssVars: CustomCssVarsFn = ({ styleParams, isMobile, isRTL }) => {
  const {
    rewardsLayout,
    listSpacing,
    cardsRewardsVerticalPadding,
    cardsRewardsHorizontalPadding,
    listRewardsVerticalPadding,
    listRewardsHorizontalPadding,
    cardsItemsAlignment,
  } = styleParams.numbers;
  const isCardsLayout = rewardsLayout === RewardsLayout.Cards;

  const cardsLayoutAlignItemsVarMap: Record<number, string> = {
    [Alignment.Left]: 'flex-start',
    [Alignment.Center]: 'center',
    [Alignment.Right]: 'flex-end',
  };

  const cardsLayoutTextAlignVarMap: Record<number, string> = {
    [Alignment.Left]: isRTL ? 'right' : 'left',
    [Alignment.Center]: 'center',
    [Alignment.Right]: isRTL ? 'left' : 'right',
  };

  const showRewardBadgeAboveTitle = isCardsLayout || isMobile;

  return {
    rewardsLayoutDisplay: isCardsLayout ? 'grid' : 'block',
    rewardsLayoutMarginTop: isCardsLayout ? 30 : 0,
    rewardsCardLayoutGridColumns: isMobile ? '1fr' : '1fr 1fr',
    rewardsDividerDisplay: isCardsLayout ? 'none' : 'block',
    rewardVerticalMargin: isCardsLayout ? 0 : listSpacing,
    rewardVerticalPadding: isCardsLayout ? cardsRewardsVerticalPadding : listRewardsVerticalPadding,
    rewardHorizontalPadding: isCardsLayout ? cardsRewardsHorizontalPadding : listRewardsHorizontalPadding,
    rewardFlexDirection: isCardsLayout || isMobile ? 'column' : 'row',
    rewardAlignItems: isCardsLayout
      ? cardsLayoutAlignItemsVarMap[cardsItemsAlignment]
      : isMobile
      ? 'flex-start'
      : 'center',
    rewardButtonContainerMarginTop: isCardsLayout || isMobile ? 20 : 0,
    rewardContentTextAlign: isCardsLayout ? cardsLayoutTextAlignVarMap[cardsItemsAlignment] : isRTL ? 'right' : 'left',
    rewardBadgeAboveTitleDisplay: showRewardBadgeAboveTitle ? 'inline' : 'none',
    rewardBadgeBehindTitleDisplay: showRewardBadgeAboveTitle ? 'none' : 'inline-flex',
    rewardBadgeMarginLeft: !isMobile && !isCardsLayout ? 8 : 0,
    rewardBadgeMarginBottom: isCardsLayout ? 20 : isMobile ? 8 : 0,
    rewardProgressAlignItems: isCardsLayout
      ? cardsLayoutAlignItemsVarMap[cardsItemsAlignment]
      : isMobile
      ? 'flex-start'
      : 'flex-end',
    rewardButtonMaxWidth: isMobile ? 250 : 400,
    widgetPadding: isMobile ? 0 : 30,
    widgetMarginTop: isMobile ? 20 : 0,
    pointsSectionGridTemplateColumns: isMobile ? '1fr' : '5fr 3fr',
  };
};
